import axios from 'axios';

const api = axios.create({
  baseURL: window.location.origin,
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' },
});

// Mutable ref updated by BrandContext when the active brand changes
let _activeBrand = null;
export function setApiActiveBrand(brand) {
  _activeBrand = brand;
}

api.interceptors.request.use((config) => {
  if (_activeBrand) {
    config.headers['X-Brand'] = _activeBrand;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && window.location.pathname !== '/login') {
      const redirect = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = `/login?redirect=${redirect}`;
    }
    return Promise.reject(error);
  }
);

export const conversionAPI = {
  getBonusConversion: (userId) => api.get(`/api/conversion/bonus/${userId}`),
};

export const gameDepartmentAPI = {
  getGameStickiness: () => api.get('/api/game-department/game-stickiness'),
  // producer/featureGroup are optional filters (feature groups are per producer).
  getGameMetrics: ({ from, to, producer, featureGroup }) =>
    api.get('/api/game-department/game-metrics', { params: { from, to, producer, feature_group: featureGroup } }),
  getProviderMetrics: ({ from, to, producer, featureGroup }) =>
    api.get('/api/game-department/provider-metrics', { params: { from, to, producer, feature_group: featureGroup } }),
  getProducerMetrics: ({ from, to, producer, featureGroup }) =>
    api.get('/api/game-department/producer-metrics', { params: { from, to, producer, feature_group: featureGroup } }),
  getFeatureGroupMetrics: ({ from, to, producer, featureGroup }) =>
    api.get('/api/game-department/feature-group-metrics', { params: { from, to, producer, feature_group: featureGroup } }),
  getFeatureGroups: () => api.get('/api/game-department/feature-groups'),
  searchGames: (q, signal) => api.get('/api/game-department/games/search', { params: { q }, signal }),
  getGamePerformance: ({ uniqueGameId, from, to, bucket }) =>
    api.get('/api/game-department/game-performance', { params: { unique_game_id: uniqueGameId, from, to, bucket } }),
  getSpendStickiness: () => api.get('/api/game-department/spend-stickiness'),
  getStickinessCurve: ({ uniqueGameId }) =>
    api.get('/api/game-department/stickiness-curve', { params: { unique_game_id: uniqueGameId } }),
  getAskOptions: () => api.get('/api/game-department/ask-options'),
  getGameReleases: ({ from, to, windowDays }) =>
    api.get('/api/game-department/releases', { params: { from, to, window_days: windowDays } }),
  getReleaseCurves: ({ from, to }) => api.get('/api/game-department/release-curves', { params: { from, to } }),
};

// Game Analysis — what bi-game-analysis captured (rules, bets per currency, bonus buys), read by the server
// from its database and bucket. Screenshots are plain <img> URLs (shotUrl).
export const gameAnalysisAPI = {
  // params: query, provider, sort (title|rtp|max_win|completeness|assembled), desc, limit, offset
  getGames: (params, signal) => api.get('/api/game-analysis/games', { params, signal }),
  getProviders: () => api.get('/api/game-analysis/providers'),
  getGame: (slug) => api.get(`/api/game-analysis/games/${encodeURIComponent(slug)}`),
};

export const vipAPI = {
  getAgents: () => api.get('/api/vip/agents'),
  getDashboard: (agent, params = {}) => api.get('/api/vip/dashboard', { params: { agent, ...params } }),
  getNonVipDashboard: (params = {}) => api.get('/api/vip/dashboard/non-vip', { params }),
  getSettings: () => api.get('/api/vip/settings'),
  saveSettings: (settings) => api.put('/api/vip/settings', settings),
  getPobaHistory: (userId, signal) => api.get(`/api/vip/poba-history/${userId}`, { signal }),
  getPlayerStats: (userId, signal) => api.get(`/api/vip/player/${userId}`, { signal }),
  getPlayerStatsRange: (userId, { from, to, compare = true }, signal) =>
    api.get(`/api/vip/player/${userId}/stats-range`, { params: { from, to, compare }, signal }),
  getPlayerGames: (userId, days = 7, signal) => api.get(`/api/vip/player/${userId}/games`, { params: { days }, signal }),
  searchPlayers: (q) => api.get('/api/vip/player/search', { params: { q } }),
  getAnniversaryBonuses: (params = {}) => api.get('/api/vip/anniversary-bonuses', { params }),
  // body: { action: 'override' | 'approve' | 'reject' | 'reset', amount_usd_cents, note }
  moderateAnniversaryBonus: (id, body) => api.put(`/api/vip/anniversary-bonuses/${id}`, body),
  // params: year | from, status, bonus_type, vip_agent, user_id, page, page_size; returns items + totals over all matches
  getChristmasBonuses: (params = {}) => api.get('/api/vip/christmas-bonuses', { params }),
  moderateChristmasBonus: (id, body) => api.put(`/api/vip/christmas-bonuses/${id}`, body),
  // q: user ID or email; returns the player card, the VIP comp checks and the proposal (BitStarz)
  getBonusRequestCheck: (q) => api.get('/api/vip/bonus-requests/check', { params: { q } }),
  // body: { user_id, amount?, wagering?, comment?, exception_reason? }, or with override: true also
  // currency and a required comment (issuing although a check failed); saves the comp or corrects a pending one
  issueBonus: (body) => api.post('/api/vip/bonus-requests/issue', body),
  // cancels a pending comp (row id); 409 once it was pushed
  cancelBonus: (id) => api.post(`/api/vip/bonus-requests/${id}/cancel`),
  // params: status (pending | pushed | cancelled), q (user ID or email), page, page_size; newest first, with total
  getIssuedBonuses: (params = {}) => api.get('/api/vip/bonus-requests/issued', { params }),
  getIssuedBonusStats: (params = {}) => api.get('/api/vip/bonus-requests/stats', { params }),
  // params: activity (bet | login | deposit | bonus), tier, state (due | overdue | never | upcoming), sort
  // (ngr_30d | ngr_total), search, page, page_size; the agent's players in queue order, plus summary counts
  getContactTracker: (agent, params = {}, signal) =>
    api.get('/api/vip/contact-tracker', { params: { agent, ...params }, signal }),
  // params: agent (omit for every manager), search (user ID or text), user_id, channel_id, days | from + to,
  // page, page_size; newest first, with total and a summary of every match
  getContactLog: (params = {}, signal) => api.get('/api/vip/contacts', { params, signal }),
  getContactChannels: () => api.get('/api/vip/contacts/channels'),
  // identifiers: pasted emails / user IDs; returns { players, not_found, invalid } (nothing saved)
  resolveContactPlayers: (identifiers) => api.post('/api/vip/contacts/resolve', { identifiers }),
  // body: { user_ids, channel_id, contacted_at, next_contact_at?, note? }; one row per player
  logContactBatch: (body) => api.post('/api/vip/contacts/batch', body),
  getPlayerContacts: (userId, signal) => api.get(`/api/vip/player/${userId}/contacts`, { signal }),
  // body: { channel_id, contacted_at: 'YYYY-MM-DD', next_contact_at?, note? }; the author is the session user
  logContact: (userId, body) => api.post(`/api/vip/player/${userId}/contacts`, body),
};

export const notificationAPI = {
  getList: (params) => api.get('/api/notifications', { params }),
  getById: (id) => api.get(`/api/notifications/${id}`),
  getUnreadCount: () => api.get('/api/notifications/unread-count'),
  markRead: (id) => api.put(`/api/notifications/${id}/read`),
  markAllRead: (params) => api.put('/api/notifications/read-all', null, { params }),
  getBrands: () => api.get('/api/notifications/brands'),
  getTitles: () => api.get('/api/notifications/titles'),
  getVapidPublicKey: () => api.get('/api/notifications/push/vapid-public-key'),
  subscribePush: (body) => api.post('/api/notifications/push/subscribe', body),
  unsubscribePush: (body) => api.post('/api/notifications/push/unsubscribe', body),
};

export const casinoIssuesAPI = {
  list: (params) => api.get('/api/casino-issues', { params }),
  create: (body) => api.post('/api/casino-issues', body),
  update: (id, body) => api.put(`/api/casino-issues/${id}`, body),
};

// Sends freespin games JSON exactly as typed, as {"games": <text>}. Parsing it to build the body
// would round integers above 2^53; the caller has checked it is valid JSON.
const putGamesText = (url, gamesText) =>
  api.put(url, `{"games":${gamesText}}`, { headers: { 'Content-Type': 'application/json' } });

// Rows are keyed by (tournament_id, user_id) — a player has at most one exception per tournament.
export const crmCampaignsAPI = {
  listTeslaExceptions: (params) => api.get('/api/crm-campaigns/tesla-tickets/exceptions', { params }),
  createTeslaException: (body) => api.post('/api/crm-campaigns/tesla-tickets/exceptions', body),
  updateTeslaException: (tournamentId, userId, body) =>
    api.put(`/api/crm-campaigns/tesla-tickets/exceptions/${tournamentId}/${userId}`, body),
  deleteTeslaException: (tournamentId, userId) =>
    api.delete(`/api/crm-campaigns/tesla-tickets/exceptions/${tournamentId}/${userId}`),

  // Cashback weekend limits are keyed by currency; the timeframe is a single row.
  listCashbackLimits: () => api.get('/api/crm-campaigns/cashback-weekend/limits'),
  createCashbackLimit: (body) => api.post('/api/crm-campaigns/cashback-weekend/limits', body),
  updateCashbackLimit: (currency, body) =>
    api.put(`/api/crm-campaigns/cashback-weekend/limits/${encodeURIComponent(currency)}`, body),
  deleteCashbackLimit: (currency) =>
    api.delete(`/api/crm-campaigns/cashback-weekend/limits/${encodeURIComponent(currency)}`),
  getCashbackTimeframe: () => api.get('/api/crm-campaigns/cashback-weekend/timeframe'),
  setCashbackTimeframe: (body) => api.put('/api/crm-campaigns/cashback-weekend/timeframe', body),
  listLiveCasinoGames: () => api.get('/api/crm-campaigns/cashback-weekend/live-casino-games'),
  // VIP Release: one games JSON; limits keyed by rank range (from, to); bet levels by currency.
  getVipReleaseGames: () => api.get('/api/crm-campaigns/vip-release/games'),
  setVipReleaseGames: (gamesText) => putGamesText('/api/crm-campaigns/vip-release/games', gamesText),
  listVipReleaseLimits: () => api.get('/api/crm-campaigns/vip-release/limits'),
  createVipReleaseLimit: (body) => api.post('/api/crm-campaigns/vip-release/limits', body),
  updateVipReleaseLimit: (from, to, body) =>
    api.put(`/api/crm-campaigns/vip-release/limits/${encodeURIComponent(from)}/${encodeURIComponent(to)}`, body),
  deleteVipReleaseLimit: (from, to) =>
    api.delete(`/api/crm-campaigns/vip-release/limits/${encodeURIComponent(from)}/${encodeURIComponent(to)}`),
  listVipReleaseBetLevels: () => api.get('/api/crm-campaigns/vip-release/bet-levels'),
  createVipReleaseBetLevel: (body) => api.post('/api/crm-campaigns/vip-release/bet-levels', body),
  updateVipReleaseBetLevel: (currency, body) =>
    api.put(`/api/crm-campaigns/vip-release/bet-levels/${encodeURIComponent(currency)}`, body),
  deleteVipReleaseBetLevel: (currency) =>
    api.delete(`/api/crm-campaigns/vip-release/bet-levels/${encodeURIComponent(currency)}`),
  // Second Chance Freespins: one games JSON, like VIP Release's.
  getSecondChanceGames: () => api.get('/api/crm-campaigns/second-chance-freespins/games'),
  setSecondChanceGames: (gamesText) => putGamesText('/api/crm-campaigns/second-chance-freespins/games', gamesText),
  // Limits sit under one fixed A/B test group and currency (the list reports them); a row is
  // keyed by its deposit lower bound, and the backend derives its upper end.
  listSecondChanceLimits: () => api.get('/api/crm-campaigns/second-chance-freespins/limits'),
  createSecondChanceLimit: (body) => api.post('/api/crm-campaigns/second-chance-freespins/limits', body),
  updateSecondChanceLimit: (from, body) =>
    api.put(`/api/crm-campaigns/second-chance-freespins/limits/${encodeURIComponent(from)}`, body),
  deleteSecondChanceLimit: (from) =>
    api.delete(`/api/crm-campaigns/second-chance-freespins/limits/${encodeURIComponent(from)}`),
  // Friday Loyalty Bonus: one games JSON; limits (fixed group and currency, keyed by loss lower
  // bound, with one deposit threshold for all); max amounts by currency; tag values by tag name
  // (a query parameter, since a name may hold a "/").
  getFridayLoyaltyGames: () => api.get('/api/crm-campaigns/friday-loyalty/games'),
  setFridayLoyaltyGames: (gamesText) => putGamesText('/api/crm-campaigns/friday-loyalty/games', gamesText),
  listFridayLoyaltyLimits: () => api.get('/api/crm-campaigns/friday-loyalty/limits'),
  createFridayLoyaltyLimit: (body) => api.post('/api/crm-campaigns/friday-loyalty/limits', body),
  updateFridayLoyaltyLimit: (lossFrom, body) =>
    api.put(`/api/crm-campaigns/friday-loyalty/limits/${encodeURIComponent(lossFrom)}`, body),
  deleteFridayLoyaltyLimit: (lossFrom) =>
    api.delete(`/api/crm-campaigns/friday-loyalty/limits/${encodeURIComponent(lossFrom)}`),
  // The deposit threshold every limit carries, set for all of them at once.
  setFridayLoyaltyDeposit: (body) => api.put('/api/crm-campaigns/friday-loyalty/deposit', body),
  listFridayLoyaltyMaxAmounts: () => api.get('/api/crm-campaigns/friday-loyalty/max-amounts'),
  createFridayLoyaltyMaxAmount: (body) => api.post('/api/crm-campaigns/friday-loyalty/max-amounts', body),
  updateFridayLoyaltyMaxAmount: (currency, body) =>
    api.put(`/api/crm-campaigns/friday-loyalty/max-amounts/${encodeURIComponent(currency)}`, body),
  deleteFridayLoyaltyMaxAmount: (currency) =>
    api.delete(`/api/crm-campaigns/friday-loyalty/max-amounts/${encodeURIComponent(currency)}`),
  listFridayLoyaltyTagValues: () => api.get('/api/crm-campaigns/friday-loyalty/tag-values'),
  createFridayLoyaltyTagValue: (body) => api.post('/api/crm-campaigns/friday-loyalty/tag-values', body),
  updateFridayLoyaltyTagValue: (tagName, body) =>
    api.put('/api/crm-campaigns/friday-loyalty/tag-values', body, { params: { tag_name: tagName } }),
  deleteFridayLoyaltyTagValue: (tagName) =>
    api.delete('/api/crm-campaigns/friday-loyalty/tag-values', { params: { tag_name: tagName } }),
  // Issue Test Freespins: rows are only added (queued as WAITING) and listed, newest first.
  listTestFreespins: (params) => api.get('/api/crm-campaigns/test-freespins', { params }),
  createTestFreespins: (body) => api.post('/api/crm-campaigns/test-freespins', body),
  // Applies every create rule (the currency lookup too) without inserting; 204 when it passes.
  checkTestFreespins: (body) => api.post('/api/crm-campaigns/test-freespins/check', body),
  // Wednesday Freespins: games rows by priority (1 can be switched off); limits by currency,
  // one deposit amount per freespin tier.
  listWednesdayGames: () => api.get('/api/crm-campaigns/wednesday-freespins/games'),
  setWednesdayGames: (priority, gamesText) => putGamesText(`/api/crm-campaigns/wednesday-freespins/games/${priority}`, gamesText),
  setWednesdayGamesActive: (priority, active) =>
    api.put(`/api/crm-campaigns/wednesday-freespins/games/${priority}/active`, { active }),
  listWednesdayLimits: () => api.get('/api/crm-campaigns/wednesday-freespins/limits'),
  createWednesdayLimits: (body) => api.post('/api/crm-campaigns/wednesday-freespins/limits', body),
  updateWednesdayLimits: (currency, body) =>
    api.put(`/api/crm-campaigns/wednesday-freespins/limits/${encodeURIComponent(currency)}`, body),
  deleteWednesdayLimits: (currency) =>
    api.delete(`/api/crm-campaigns/wednesday-freespins/limits/${encodeURIComponent(currency)}`),
  // The weekly freespins drop's limits, shown with Wednesday's: USD only, one PUT for its tiers.
  listWeeklyLimits: () => api.get('/api/crm-campaigns/wednesday-freespins/weekly-limits'),
  setWeeklyLimits: (body) => api.put('/api/crm-campaigns/wednesday-freespins/weekly-limits', body),
};

// Private VIP dashboard: one agent's book (fixed by the brand config) read from the temp_ VIP
// tables. No agent parameter anywhere; per-player calls answer 403 outside the book.
export const vipPrivateAPI = {
  getDashboard: (params = {}) => api.get('/api/vip-private/dashboard', { params }),
  searchPlayers: (q) => api.get('/api/vip-private/player/search', { params: { q } }),
  getPlayerStats: (userId, signal) => api.get(`/api/vip-private/player/${userId}`, { signal }),
  getPlayerStatsRange: (userId, { from, to, compare = true }, signal) =>
    api.get(`/api/vip-private/player/${userId}/stats-range`, { params: { from, to, compare }, signal }),
  getPlayerGames: (userId, days = 7, signal) => api.get(`/api/vip-private/player/${userId}/games`, { params: { days }, signal }),
  getHeatmapPlayer: (userId, signal) => api.get(`/api/vip-private/heatmap/player/${userId}`, { signal }),
  getTrajectoryPlayer: (userId, signal) => api.get(`/api/vip-private/trajectory/player/${userId}`, { signal }),
  // The player's accounts (with today's USD rate per currency) and the form defaults.
  getBonusForm: (userId, signal) => api.get(`/api/vip-private/player/${userId}/bonus-form`, { signal }),
  // body: { user_id, title, currency, amount (decimal string), wagering, duration_days, comment }
  issueBonus: (body) => api.post('/api/vip-private/bonuses', body),
  // params: status (pending | pushed | issued | cancelled), page, page_size; newest first, with total
  listBonuses: (params = {}, signal) => api.get('/api/vip-private/bonuses', { params, signal }),
  cancelBonus: (id) => api.post(`/api/vip-private/bonuses/${id}/cancel`),
};

export const trajectoryAPI = {
  getPlayers: (agent, days = 180) => api.get('/api/vip/trajectory/players', { params: { agent, days } }),
  getPlayer: (userId, signal) => api.get(`/api/vip/trajectory/player/${userId}`, { signal }),
};

export const heatmapAPI = {
  getPlayers: (agent) => api.get('/api/vip/heatmap/players', { params: { agent } }),
  getPlayer: (userId, signal) => api.get(`/api/vip/heatmap/player/${userId}`, { signal }),
};

export const livechatMiningAPI = {
  getDateBounds: () => api.get('/api/livechat-mining/date-bounds'),
  getSummary: (from, to) => api.get('/api/livechat-mining/summary', { params: { from, to } }),
  getPatternReport: (from, to, actionability, signalType, topic, search) => api.get(
    '/api/livechat-mining/patterns',
    { params: { from, to, actionability, signal_type: signalType, topic, search } },
  ),
  getPatternThreads: (
    pattern, from, to, lifecycleState, activityState, actionability, signalType, topic, search, sortOrder,
    page = 1, pageSize = 25,
  ) => api.get(
    '/api/livechat-mining/patterns/threads',
    {
      params: {
        pattern, from, to, lifecycle_state: lifecycleState, activity_state: activityState, actionability,
        signal_type: signalType, topic, search, sort: sortOrder, page, page_size: pageSize,
      },
    },
  ),
  getPatternSignals: (
    pattern, from, to, lifecycleState, activityState, actionability, signalType, topic, search,
  ) => api.get(
    '/api/livechat-mining/patterns/signals',
    {
      params: {
        pattern, from, to, lifecycle_state: lifecycleState, activity_state: activityState, actionability,
        signal_type: signalType, topic, search,
      },
    },
  ),
  getPatternSegments: (
    pattern, from, to, lifecycleState, activityState, actionability, signalType, topic, search,
  ) => api.get(
    '/api/livechat-mining/patterns/segments',
    {
      params: {
        pattern, from, to, lifecycle_state: lifecycleState, activity_state: activityState, actionability,
        signal_type: signalType, topic, search,
      },
    },
  ),
  getPatternExport: (
    pattern, from, to, lifecycleState, activityState, actionability, signalType, topic, search, sortOrder,
  ) => api.get(
    '/api/livechat-mining/patterns/export',
    {
      params: {
        pattern, from, to, lifecycle_state: lifecycleState, activity_state: activityState, actionability,
        signal_type: signalType, topic, search, sort: sortOrder,
      },
    },
  ),
  getPatternTrend: (
    pattern, bucket, lifecycleState, activityState, actionability, signalType, topic, search,
  ) => api.get(
    '/api/livechat-mining/patterns/trend',
    {
      params: {
        pattern, bucket, lifecycle_state: lifecycleState, activity_state: activityState, actionability,
        signal_type: signalType, topic, search,
      },
    },
  ),
  getPatternHousekeeping: () => api.get('/api/livechat-mining/pattern-housekeeping'),
  getThreadConversation: (threadId) => api.get(
    `/api/livechat-mining/threads/${encodeURIComponent(threadId)}/conversation`,
  ),
};

// The Flagged Chats filter set as query params, shared by getThreads and exportThreads. Every
// field is optional; omitted means no filter on it. flagTypes is an array — a thread must
// carry ALL of them (AND), not just one. countries is also an array, but OR — a thread matches
// if its country is any of them (a thread only ever has one). checked is '', 'checked', or
// 'unchecked'. rating is '', 'positive', 'negative', or 'not_rated'. hasComment is '', 'yes',
// or 'no'. checkedBy is a reviewer's email — only meaningful alongside checked='checked'.
// threadID is an exact ID match — "search by conversation ID".
const flaggedThreadParams = ({
  from, to, flagTypes = [], agent = '', sortBy = 'start_at', sortDir = 'desc', checked = '', rating = '',
  hasComment = '', countries = [], checkedBy = '', threadID = '',
}) => ({
  from, to, flag_type: flagTypes.join(','), agent, sort_by: sortBy, sort_dir: sortDir,
  checked, rating, has_comment: hasComment, country: countries.join(','),
  checked_by: checkedBy, thread_id: threadID,
});

export const flaggedChatsAPI = {
  getDateBounds: () => api.get('/api/flagged-chats/date-bounds'),
  getFlagTypes: () => api.get('/api/flagged-chats/flag-types'),
  getAgents: () => api.get('/api/flagged-chats/agents'),
  // Only meaningful threads have a checked_by, so this list only ever grows alongside the
  // "checked" status filter — populates the "Checked by" filter.
  getCheckedByOptions: () => api.get('/api/flagged-chats/checked-by'),
  // Distinct country values seen on the brand's chats — populates the "Country" filter.
  getCountries: () => api.get('/api/flagged-chats/countries'),
  // Takes the filters as one named object (see flaggedThreadParams) rather than positionally:
  // with a dozen mostly-string filters, a positional call silently shifts every value after a
  // newly inserted parameter — which is how Agent Analysis once sent its page number as a
  // conversation-ID search and listed nothing.
  getThreads: ({ page = 1, pageSize = 25, ...filters }) => api.get(
    '/api/flagged-chats/threads',
    { params: { ...flaggedThreadParams(filters), page, page_size: pageSize } },
  ),
  getThreadConversation: (threadId) => api.get(
    `/api/flagged-chats/threads/${encodeURIComponent(threadId)}/conversation`,
  ),
  // Same filters as getThreads, no pagination — every matching thread (up to a server-side
  // cap) with its full conversation attached, for bulk download.
  exportThreads: (filters) => api.get(
    '/api/flagged-chats/threads/export',
    { params: flaggedThreadParams(filters) },
  ),
  // Checked/unchecked is shared across every reviewer — not per-user state. startAt is the
  // thread's own start_at (already in hand from the list), stored so the "checked" filter
  // can narrow by date directly in Postgres instead of scanning Redshift for it.
  setThreadChecked: (threadId, checked, startAt) => api.put(
    `/api/flagged-chats/threads/${encodeURIComponent(threadId)}/checked`,
    { checked, start_at: startAt },
  ),
  // agent omitted/empty means every agent combined ("All agents").
  getAgentTrend: (agent, bucket, from, to) => api.get(
    '/api/flagged-chats/agent-trend',
    { params: { agent, bucket, from, to } },
  ),
};

export default api;
